// Integrated Supabase + Hetzner Storage Service (Refactored & Modular)
import { supabase } from '@config/supabase';
import { AudioFile, UploadProgress } from '@types';
import { audioService } from './audio.service';
import { storageApiService } from './storage-api.service';
import { folderService } from './folder.service';

type SupabaseUpdate = Record<string, any>;

class SupabaseAWSService {
  // Delegate properties to storageApiService for backward compatibility
  get storageBaseUrl() {
    return storageApiService.storageBaseUrl;
  }
  get urlCache() {
    return storageApiService.urlCache;
  }

  isConfigured(): boolean {
    return storageApiService.isConfigured();
  }

  /**
   * Generate object-storage key for file
   */
  generateS3Key(userId: string, fileName: string): string {
    const timestamp = Date.now();
    const cleanFileName = fileName.replace(/[^a-zA-Z0-9.-]/g, '_');
    return `${userId}/${timestamp}-${cleanFileName}`;
  }

  /**
   * Get public CDN url (Hetzner does not support public CDN; we use signed URLs)
   */
  getCDNUrl(_s3Key: string): string {
    return '';
  }

  /**
   * Main File Upload (database record + audio analysis + storage upload)
   */
  async uploadFile(
    file: File,
    metadata: Partial<AudioFile> & {
      id?: string;
      type?: 'beat' | 'sample' | 'loop';
      folderId?: string;
      tags?: any[];
      bpm?: number;
      key?: string;
    },
    onProgress?: (progress: UploadProgress) => void
  ): Promise<AudioFile> {
    console.log('🚀 [SupabaseAWS] Starting upload:', file.name);
    console.log('📊 [SupabaseAWS] Metadata:', metadata);
    
    const user = (await supabase.auth.getUser()).data.user;
    if (!user) {
      console.error('❌ [SupabaseAWS] User not authenticated');
      throw new Error('User not authenticated');
    }

    console.log('✅ [SupabaseAWS] User authenticated:', user.email);

    const fileId = metadata.id || crypto.randomUUID();
    const s3Key = this.generateS3Key(user.id, file.name);

    try {
      // Step 1: Analyze audio
      onProgress?.({
        fileId,
        fileName: file.name,
        progress: 10,
        status: 'uploading',
      });

      console.log('🎵 [SupabaseAWS] Analyzing audio...');
      const duration = await this.getAudioDuration(file);
      const analysisResult = await audioService.analyzeAudio(file);
      const waveform = Array.isArray(analysisResult) ? analysisResult : (analysisResult?.waveform || []);
      console.log('✅ [SupabaseAWS] Audio analyzed. Duration:', duration, 'Waveform points:', waveform.length);

      onProgress?.({
        fileId,
        fileName: file.name,
        progress: 30,
        status: 'uploading',
      });

      // Step 2: Start BPM/Key analysis in parallel with upload
      let finalBpm = metadata.bpm;
      let finalKey = metadata.key;
      let analysisPromise: Promise<void> | null = null;

      const hasWizardMetadata =
        (metadata.bpm != null && metadata.bpm > 0) ||
        (metadata.key != null && String(metadata.key).trim().length > 0);

      if (!hasWizardMetadata && (!finalBpm || !finalKey)) {
        console.log('🎵 [SupabaseAWS] Starting BPM/Key analysis in parallel with upload...');
        const { metadataService } = await import('./metadata.service');
        
        analysisPromise = metadataService.analyzeAudio(file, user.id).then((metaResult) => {
          console.log('📥 [SupabaseAWS] Meta API result (parallel analysis)', metaResult);
          if (metaResult.success) {
            if (!finalBpm && metaResult.bpm && metaResult.bpm > 0) {
              finalBpm = Math.max(50, Math.min(220, metaResult.bpm));
            }
            if (!finalKey && metaResult.key && metaResult.key.trim().length > 0) {
              finalKey = metaResult.key.trim();
            }
          }
        }).catch((error) => {
          console.error('❌ [SupabaseAWS] Meta API analysis error:', error);
        });
      }

      // Step 3: Upload to storage
      console.log('☁️ [SupabaseStorage] Uploading to storage:', s3Key);
      await this.uploadToStorage(file, s3Key, user.id, (progress) => {
        onProgress?.({
          fileId,
          fileName: file.name,
          progress: 30 + (progress * 0.5), // 30-80%
          status: 'uploading',
        });
      });
      console.log('✅ [SupabaseStorage] Storage upload complete');

      const cdnUrl = this.getCDNUrl(s3Key);

      onProgress?.({
        fileId,
        fileName: file.name,
        progress: 70,
        status: 'uploading',
      });

      // Step 4: Wait for parallel analysis
      if (analysisPromise) {
        await analysisPromise;
      }

      onProgress?.({
        fileId,
        fileName: file.name,
        progress: 85,
        status: 'uploading',
      });

      // Step 5: Save metadata to database
      const fileStatus = metadata.status !== undefined && metadata.status !== null 
        ? (typeof metadata.status === 'string' ? metadata.status : String(metadata.status))
        : 'wip';
      
      const insertData = {
        id: fileId,
        user_id: user.id,
        folder_id: metadata.folderId || null,
        name: metadata.name || file.name.replace(/\.[^/.]+$/, ''),
        original_name: metadata.originalName || file.name,
        type: metadata.type || 'beat',
        size: file.size,
        duration,
        format: file.type.split('/')[1] || 'mp3',
        s3_key: s3Key,
        cdn_url: cdnUrl,
        waveform: (waveform || []) as any,
        bpm: finalBpm || null,
        key: finalKey || null,
        is_main_version: metadata.isMainVersion || false,
        version_type: metadata.versionType || null,
        group_id: metadata.groupId || null,
        status: fileStatus,
        beatstars_sell_url: (metadata as any).beatstarsSellUrl?.trim() || null,
        beatstars_price: (metadata as any).beatstarsPrice?.trim() || null,
        collaborator: (metadata as any).collaborator?.trim() || null,
      };
      
      const { data: fileData, error: fileError } = await supabase
        .from('files')
        .insert(insertData as any)
        .select()
        .single();

      if (fileError) {
        throw new Error(`Database error: ${fileError.message}`);
      }
      
      console.log('✅ [SupabaseAWS] Database save complete');

      // Step 6: Process tags
      if (metadata.tags && metadata.tags.length > 0) {
        const tagIds: string[] = [];
        
        for (const tag of metadata.tags) {
          let tagId: string;
          if (typeof tag === 'object' && tag.id) {
            tagId = tag.id;
            const { data: existingTag } = await supabase
              .from('tags')
              .select('id, label')
              .eq('label', (tag as any).label)
              .maybeSingle();
            
            if (existingTag) {
              tagId = existingTag.id;
            } else {
              const newTagId = crypto.randomUUID();
              const { data: newTag, error: createError } = await supabase
                .from('tags')
                .insert({
                  id: newTagId,
                  label: (tag as any).label || (tag as any).name || 'Unknown',
                  type: (tag as any).type || 'custom',
                  color: (tag as any).color || '#888888',
                })
                .select()
                .single();
              
              if (!createError && newTag) {
                tagId = newTagId;
              }
            }
            tagIds.push(tagId);
          } else if (typeof tag === 'string') {
            tagIds.push(tag);
          }
        }
        
        if (tagIds.length > 0) {
          const linksToInsert = tagIds.map((tid: string) => ({
            file_id: fileId,
            tag_id: tid,
          }));
          await supabase.from('file_tags').insert(linksToInsert as any);
        }
      }

      onProgress?.({
        fileId,
        fileName: file.name,
        progress: 100,
        status: 'completed',
      });

      return this.convertToAudioFile(fileData);
    } catch (error) {
      onProgress?.({
        fileId,
        fileName: file.name,
        progress: 0,
        status: 'error',
        error: error instanceof Error ? error.message : 'Upload failed',
      });
      throw error;
    }
  }

  /**
   * Upload raw file to Hetzner Storage (delegated)
   */
  async uploadToStorage(
    file: File,
    s3Key: string,
    userId: string,
    onProgress?: (progress: number) => void
  ): Promise<void> {
    return storageApiService.uploadToStorage(file, s3Key, userId, onProgress);
  }

  /**
   * Delete file from Hetzner Storage (delegated)
   */
  async deleteFromStorage(userId: string, s3Key: string): Promise<void> {
    return storageApiService.deleteFromStorage(userId, s3Key);
  }

  /**
   * Get all files for user
   */
  async getFiles(): Promise<AudioFile[]> {
    const user = (await supabase.auth.getUser()).data.user;
    if (!user) throw new Error('User not authenticated');

    try {
      const queryPromise = supabase
        .from('files')
        .select(`
          *,
          file_tags (
            tags (*)
          ),
          file_shares (
            id,
            is_active
          )
        `)
        .eq('user_id', user.id)
        .eq('is_trashed', false)
        .order('uploaded_at', { ascending: false });

      const timeoutPromise = new Promise<never>((_, reject) => 
        setTimeout(() => reject(new Error('Request timeout: Files query took too long')), 30000)
      );

      const { data, error } = await Promise.race([queryPromise, timeoutPromise]);

      if (error) throw error;

      const filtered = (data ?? []).filter((file) => {
        const cdnUrl: string | undefined = file.cdn_url ?? undefined;
        const legacyUrl: string | undefined = file.url ?? undefined;
        const isLegacyAws =
          (!!cdnUrl && (cdnUrl.includes('amazonaws.com') || cdnUrl.includes('cloudfront.net'))) ||
          (!!legacyUrl && (legacyUrl.includes('amazonaws.com') || legacyUrl.includes('cloudfront.net')));
        return !isLegacyAws;
      });

      return filtered.map((file) => this.convertToAudioFile(file));
    } catch (error) {
      console.error('❌ [SupabaseAWS] Failed to fetch files:', error);
      throw error instanceof Error ? error : new Error(`Failed to fetch files: ${String(error)}`);
    }
  }

  /**
   * Get single file
   */
  async getFile(id: string): Promise<AudioFile> {
    const { data, error } = await supabase
      .from('files')
      .select(`
        *,
        file_tags (
          tags (*)
        )
      `)
      .eq('id', id)
      .single();

    if (error) throw error;
    return this.convertToAudioFile(data);
  }

  /**
   * Toggle starred state
   */
  async setFileStarred(id: string, isStarred: boolean): Promise<void> {
    const { error } = await (supabase
      .from('files') as any)
      .update({ is_starred: isStarred })
      .eq('id', id);

    if (error) throw error;
  }

  /**
   * Update file details and tags
   */
  async updateFile(id: string, updates: Partial<AudioFile>): Promise<AudioFile> {
    const updateData: SupabaseUpdate = {};
    if (updates.name !== undefined) updateData.name = updates.name;
    if (updates.folderId !== undefined) updateData.folder_id = updates.folderId;
    if (updates.isStarred !== undefined) updateData.is_starred = updates.isStarred;
    if (updates.isTrashed !== undefined) updateData.is_trashed = updates.isTrashed;
    if (updates.isPublic !== undefined) updateData.is_public = updates.isPublic;
    if (updates.isMainVersion !== undefined) updateData.is_main_version = updates.isMainVersion;
    if (updates.versionType !== undefined) updateData.version_type = updates.versionType;
    if ('groupId' in updates) {
      updateData.group_id = updates.groupId === undefined ? null : updates.groupId;
    }
    if (updates.status !== undefined) updateData.status = updates.status;
    if ('versionNote' in updates) {
      updateData.version_note = updates.versionNote ?? null;
    }
    if ('versionTags' in updates) {
      updateData.version_tags = updates.versionTags ?? null;
    }
    if ('bpm' in updates) {
      updateData.bpm = updates.bpm === undefined ? null : updates.bpm;
    }
    if ('key' in updates) {
      updateData.key = updates.key === undefined ? null : updates.key;
    }
    if ('beatstarsSellUrl' in updates) {
      updateData.beatstars_sell_url = updates.beatstarsSellUrl === undefined || updates.beatstarsSellUrl === '' ? null : updates.beatstarsSellUrl;
    }
    if ('beatstarsPrice' in updates) {
      updateData.beatstars_price = updates.beatstarsPrice === undefined || updates.beatstarsPrice === '' ? null : updates.beatstarsPrice;
    }
    if ('collaborator' in updates) {
      updateData.collaborator = updates.collaborator === undefined || updates.collaborator === '' ? null : updates.collaborator;
    }

    let data: any = null;

    if (Object.keys(updateData).length > 0) {
      const { data: updateResult, error } = await (supabase
        .from('files') as any)
        .update(updateData)
        .eq('id', id)
        .select()
        .single();

      if (error) throw error;
      data = updateResult;
    }

    if (updates.tags !== undefined) {
      await supabase.from('file_tags').delete().eq('file_id', id);
      
      if (updates.tags && updates.tags.length > 0) {
        const tagIds: string[] = [];
        for (const tag of updates.tags) {
          let tagId: string;
          if (typeof tag === 'object' && tag.id) {
            tagId = tag.id;
            const { data: existingTag } = await supabase
              .from('tags')
              .select('id, label')
              .eq('label', (tag as any).label)
              .maybeSingle();
            
            if (existingTag) {
              tagId = existingTag.id;
            } else {
              const newTagId = crypto.randomUUID();
              const { data: newTag } = await supabase
                .from('tags')
                .insert({
                  id: newTagId,
                  label: (tag as any).label || (tag as any).name || 'Unknown',
                  type: (tag as any).type || 'custom',
                  color: (tag as any).color || '#888888',
                })
                .select()
                .single();
              if (newTag) tagId = newTagId;
            }
            tagIds.push(tagId);
          } else if (typeof tag === 'string') {
            tagIds.push(tag);
          }
        }
        
        if (tagIds.length > 0) {
          const linksToInsert = tagIds.map((tid: string) => ({
            file_id: id,
            tag_id: tid,
          }));
          await supabase.from('file_tags').insert(linksToInsert as any);
        }
      }
    }

    if (!data) {
      const { data: refreshedData } = await supabase.from('files').select('*, file_tags(tags(*))').eq('id', id).single();
      data = refreshedData;
    }

    return this.convertToAudioFile(data);
  }

  /**
   * Delete file permanently or move to trash
   */
  async deleteFile(id: string, permanent: boolean = false): Promise<void> {
    console.log(`🗑️ [SupabaseAWS] Deleting file: ${id} (permanent: ${permanent})`);

    const { data: file, error: fetchError } = await supabase
      .from('files')
      .select('user_id, s3_key, name')
      .eq('id', id)
      .single();

    if (fetchError || !file) {
      throw new Error(`File not found: ${id}`);
    }

    if (permanent) {
      // Step 1: Delete file_tags relationships
      const { error: tagDeleteError } = await supabase
        .from('file_tags')
        .delete()
        .eq('file_id', id);

      if (tagDeleteError) {
        console.error('❌ Error deleting file_tags relationships:', tagDeleteError);
      }

      // Step 2: Delete versions references
      await supabase.from('file_versions').delete().eq('version_file_id', id);
      await supabase.from('file_versions').delete().eq('parent_file_id', id);

      // Step 3: Delete database record
      const { error: dbDeleteError } = await supabase
        .from('files')
        .delete()
        .eq('id', id);

      if (dbDeleteError) {
        throw dbDeleteError;
      }

      // Step 4: Delete from physical storage
      if (file.s3_key) {
        await this.deleteFromStorage(file.user_id, file.s3_key);
      }
    } else {
      // Soft trash
      const { error } = await (supabase
        .from('files') as any)
        .update({ is_trashed: true, trashed_at: new Date().toISOString() })
        .eq('id', id);

      if (error) throw error;
    }
  }

  /**
   * Move file to another folder
   */
  async moveFile(id: string, folderId: string | null): Promise<void> {
    const { error } = await (supabase
      .from('files') as any)
      .update({ folder_id: folderId })
      .eq('id', id);

    if (error) throw error;
  }

  /**
   * Search files for user
   */
  async searchFiles(query: string): Promise<AudioFile[]> {
    const user = (await supabase.auth.getUser()).data.user;
    if (!user) throw new Error('User not authenticated');

    const { data, error } = await supabase
      .from('files')
      .select(`
        *,
        file_tags (
          tags (*)
        )
      `)
      .eq('user_id', user.id)
      .eq('is_trashed', false)
      .ilike('name', `%${query}%`);

    if (error) throw error;
    return (data || []).map((file) => this.convertToAudioFile(file));
  }

  /**
   * Get single signed url (delegated)
   */
  async getSignedUrl(s3Key: string, ownerId?: string, allowPublicAccess: boolean = false, bypassCache: boolean = false): Promise<string> {
    return storageApiService.getSignedUrl(s3Key, ownerId, allowPublicAccess, bypassCache);
  }

  /**
   * Batch get signed urls (delegated)
   */
  async getSignedUrlsBatch(s3Keys: string[], ownerId?: string): Promise<Map<string, string>> {
    return storageApiService.getSignedUrlsBatch(s3Keys, ownerId);
  }

  /**
   * Preload signed urls (delegated)
   */
  async preloadSignedUrls(files: AudioFile[], maxFiles: number = 60): Promise<void> {
    return storageApiService.preloadSignedUrls(files, maxFiles);
  }

  /**
   * Folder CRUD operations (delegated to folderService)
   */
  async getFolders(): Promise<any[]> {
    return folderService.getFolders();
  }

  async createFolder(name: string, parentId?: string): Promise<any> {
    return folderService.createFolder(name, parentId);
  }

  async updateFolder(id: string, updates: any): Promise<any> {
    return folderService.updateFolder(id, updates);
  }

  async deleteFolder(id: string): Promise<void> {
    return folderService.deleteFolder(id, this.deleteFile.bind(this));
  }

  /**
   * Get duration of audio file
   */
  private async getAudioDuration(file: File): Promise<number> {
    return new Promise((resolve) => {
      const audio = new Audio();
      const objectUrl = URL.createObjectURL(file);
      
      audio.src = objectUrl;
      audio.addEventListener('loadedmetadata', () => {
        URL.revokeObjectURL(objectUrl);
        resolve(audio.duration);
      });
      
      audio.addEventListener('error', () => {
        URL.revokeObjectURL(objectUrl);
        resolve(0); // Fallback if meta cannot be parsed
      });
    });
  }

  /**
   * Helper: Convert database row to AudioFile type
   */
  private convertToAudioFile(data: any): AudioFile {
    const tags = data.file_tags?.map((ft: any) => ({
      id: ft.tags.id,
      label: ft.tags.label,
      type: ft.tags.type,
      color: ft.tags.color,
    })) || [];
    
    const isShared = data.file_shares && Array.isArray(data.file_shares) && 
      data.file_shares.some((share: any) => share.is_active === true);

    const audioFile: any = {
      id: data.id,
      userId: data.user_id,
      name: data.name,
      originalName: data.original_name,
      type: data.type,
      size: data.size,
      duration: parseFloat(data.duration),
      format: data.format,
      url: data.cdn_url || data.url,
      cdn_url: data.cdn_url,
      s3_key: data.s3_key,
      bpm: data.bpm,
      key: data.key,
      tags: tags,
      folderId: data.folder_id,
      isStarred: Boolean(data.is_starred),
      isTrashed: data.is_trashed,
      isPublic: data.is_public,
      isMainVersion: data.is_main_version,
      versionType: data.version_type,
      groupId: data.group_id,
      status: data.status,
      versionNote: data.version_note,
      versionTags: Array.isArray(data.version_tags) ? data.version_tags : (data.version_tags ? [data.version_tags] : []),
      downloads: data.downloads,
      plays: data.plays,
      uploadedAt: data.uploaded_at,
      updatedAt: data.updated_at,
      isShared: isShared,
    };

    if (data.beatstars_sell_url !== undefined) {
      const v = data.beatstars_sell_url;
      audioFile.beatstarsSellUrl = v == null || String(v).trim() === '' ? undefined : String(v).trim();
    }
    if (data.beatstars_price !== undefined) {
      const v = data.beatstars_price;
      audioFile.beatstarsPrice = v == null || String(v).trim() === '' ? undefined : String(v).trim();
    }
    if (data.collaborator !== undefined) {
      const v = data.collaborator;
      audioFile.collaborator = v == null || String(v).trim() === '' ? undefined : String(v).trim();
    }
    
    if (data.share_url) audioFile.shareUrl = data.share_url;
    
    return audioFile as AudioFile;
  }
}

export const supabaseAWSService = new SupabaseAWSService();
export default supabaseAWSService;
